import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Input, Row, SegmentControl, Spinner2, Text } from 'components';
import { Api, theme, FEATURE_NAMES } from 'lib';
import {
  WINDOW_UNITS,
  describeAppliedWindows,
  describeMinutes,
  windowChangeConfirmation,
  windowFromHours,
  windowMinutes,
  windowToHours
} from './gloryBoardWindow';
import {
  SNOOZE_UNITS,
  describeSnooze,
  snoozeError,
  snoozeFromMinutes,
  snoozeMinutes
} from './referralSnoozeDuration';
import Section from './Section';
import Toggle from './Toggle';

const DEFAULT_SETTINGS = {
  htAccuracyPoints: {
    exactMatch: 3,
    goalDiff: 2,
    correctWinner: 1,
    totalGoals: 1
  },
  ftClosenessPoints: {
    correctWinner: 3,
    goalDiff: 5,
    totalGoals: 2
  },
  ftExactScore: 10,
  endOfVoteHalfTime: 2,
  endOfVoteFullTime: 10,
  htPredictionParticipationPoints: 1,
  ftPredictionParticipationPoints: 1,
  liveCheckInPoints: 1,
  fanCamChallengePoints: 50,
  fanCamVideoPoints: 50,
  fanCamLivePoints: 30,
  quizParticipationPoints: 25,
  quizPerfectScoreBonus: 15,
  shoutoutPoints: 20,
  potmVotePoints: 10,
  gloryBoardWindowHours: 48,
  referralMilestoneThreshold: 10,
  referralBonusPoints: 500,
  // Invite prompt snooze (FanCam + Rate It), in whole minutes: two declines within it mute the
  // prompt for that long. 1440 = 24h, the server default.
  referralPromptSnoozeMinutes: 1440,
  tiri: {
    // ONE participation figure for the one cycle Tag It and Rate It share — five completions
    // in any combination. 20 mirrors the server default; the per-module figures this replaced
    // are gone from here entirely, so the CMS stops writing keys nothing reads.
    participationPoints: 20,
    // `referralPoints` stays under `rateit`. Only it has an invite flow, and the figure is
    // quoted to fans in the app's own copy, so it has no meaning at the TIRI root.
    rateit: { referralPoints: 500 }
  },
  countryExclusions: {},
  teamExclusions: {}
};

const DEFAULT_BADGE_SETTINGS = {
  tiers: {
    bronze: { minPoints: 0, maxPoints: 499 },
    silver: { minPoints: 500, maxPoints: 1499 },
    gold: { minPoints: 1500 }
  },
};

const SECTIONS = {
  htAccuracy: {
    title: 'HT Accuracy Points',
    description: 'Points awarded for half-time prediction accuracy',
    fields: [
      { key: 'exactMatch', label: 'Exact Score', desc: 'Predicted exact HT score' },
      { key: 'correctWinner', label: 'Correct Winner', desc: 'Picked winning team' },
      { key: 'goalDiff', label: 'Goal Difference', desc: 'Correct goal difference' },
      { key: 'totalGoals', label: 'Total Goals', desc: 'Correct total goals' }
    ]
  },
  ftCloseness: {
    title: 'FT Closeness Points',
    description: 'Bonus points when FT prediction is close but not exact (mutually exclusive with FT Exact, max +9)',
    fields: [
      { key: 'correctWinner', label: 'Correct Winner', desc: 'Picked winning team' },
      { key: 'goalDiff', label: 'Goal Difference', desc: 'Correct goal difference' },
      { key: 'totalGoals', label: 'Total Goals', desc: 'Correct total goals' }
    ]
  }
};

const FLAT_FIELDS = [
  { key: 'endOfVoteHalfTime', label: 'End Of Vote HT (mins)', desc: 'Minutes into game when HT voting locks', section: 'Prediction' },
  { key: 'endOfVoteFullTime', label: 'End Of Vote FT (mins)', desc: 'Minutes into game when FT voting locks', section: 'Prediction' },
  { key: 'htPredictionParticipationPoints', label: 'HT Participation', desc: 'Points for casting first HT prediction vote', section: 'Prediction' },
  { key: 'ftPredictionParticipationPoints', label: 'FT Participation', desc: 'Points for casting first FT prediction vote', section: 'Prediction' },
  { key: 'liveCheckInPoints', label: 'Daily Like Streak', desc: 'Points for daily like streak', section: 'Engagement' },
  { key: 'fanCamChallengePoints', label: 'FanCam Photo Points', desc: 'Points for FanCam Challenge photo upload', section: 'FanCam' },
  { key: 'fanCamVideoPoints', label: 'FanCam Video Points', desc: 'Points for FanCam Challenge video upload', section: 'FanCam' },
  { key: 'fanCamLivePoints', label: FEATURE_NAMES.FANCAM_LIVE.en, desc: `Points for ${FEATURE_NAMES.FANCAM_LIVE.en} participation`, section: 'FanCam' },
  { key: 'quizParticipationPoints', label: 'Quiz Participation', desc: 'Points for quiz participation', section: 'Engagement' },
  { key: 'quizPerfectScoreBonus', label: 'Quiz Perfect Bonus', desc: 'Bonus for perfect quiz score', section: 'Engagement' },
  { key: 'shoutoutPoints', label: 'Shoutout', desc: 'Points for shoutouts', section: 'Engagement' },
  { key: 'potmVotePoints', label: 'POTM Vote', desc: 'Points for Player of the Match voting', section: 'Engagement' }
];

const TOGGLE_CATEGORIES = [
  { key: 'htAccuracy', label: 'HT Accuracy' },
  { key: 'ftCloseness', label: 'FT Closeness' },
  { key: 'ftExactScore', label: 'FT Exact Score' }
];

const RewardPointsTab = () => {
  // Only the TIRI card below is translated so far — the rest of this screen is still English
  // literals, and is left alone rather than half-converted in a TIRI branch.
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // The window length as last loaded, so a save asks first only when it actually changes it.
  const [savedWindowHours, setSavedWindowHours] = useState(undefined);
  const [badgeSettings, setBadgeSettings] = useState(DEFAULT_BADGE_SETTINGS);
  const [teamInput, setTeamInput] = useState('');
  const [countryInput, setCountryInput] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchBadgeSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await Api.get('/admin/rewardPointSetting/get');
      if (res?.data?.settings) {
        setSettings((prev) => mergeSettings(prev, res.data.settings));
        setSavedWindowHours(res.data.settings.gloryBoardWindowHours);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadgeSettings = async () => {
    try {
      const res = await Api.get('/admin/badgeSettings/get');
      if (res?.data?.settings) {
        setBadgeSettings((prev) => mergeBadgeSettings(prev, res.data.settings));
      }
    } catch (error) {
      console.error('Error fetching badge settings:', error);
    }
  };

  const mergeSettings = (defaults, fetched) => {
    const { participation: _removed, ...fetchedFtCloseness } = fetched.ftClosenessPoints || {};
    return {
      ...defaults,
      ...fetched,
      htAccuracyPoints: { ...defaults.htAccuracyPoints, ...fetched.htAccuracyPoints },
      ftClosenessPoints: { ...defaults.ftClosenessPoints, ...fetchedFtCloseness },
      htPredictionParticipationPoints: fetched.htPredictionParticipationPoints ?? defaults.htPredictionParticipationPoints,
      ftPredictionParticipationPoints: fetched.ftPredictionParticipationPoints ?? defaults.ftPredictionParticipationPoints,
      endOfVoteFullTime: fetched.endOfVoteFullTime ?? defaults.endOfVoteFullTime,
      tiri: {
        // Falls back to the Tag It figure before the default: the server's migration seeds
        // `participationPoints` from it, but an admin who opens this page against an instance
        // that has not run the migration yet should still see the price they set, not a 20
        // that would overwrite it on the next save.
        participationPoints:
          fetched.tiri?.participationPoints ??
          fetched.tiri?.tagit?.participationPoints ??
          defaults.tiri.participationPoints,
        // Only `referralPoints` is picked out, rather than spreading `fetched.tiri.rateit`
        // wholesale: that object still carries the superseded `participationPoints` on any
        // instance whose document predates the merge, and carrying it into state is how a
        // dead key finds its way back into a save payload.
        rateit: {
          referralPoints:
            fetched.tiri?.rateit?.referralPoints ?? defaults.tiri.rateit.referralPoints
        }
      },
      countryExclusions: fetched.countryExclusions ?? defaults.countryExclusions,
      teamExclusions: fetched.teamExclusions ?? defaults.teamExclusions
    };
  };

  const mergeBadgeSettings = (defaults, fetched) => ({
    tiers: {
      bronze: { ...defaults.tiers.bronze, ...fetched.tiers?.bronze },
      silver: { ...defaults.tiers.silver, ...fetched.tiers?.silver },
      gold: { ...defaults.tiers.gold, ...fetched.tiers?.gold }
    },
  });

  const handleSaveAll = async () => {
    // `null` is what the Ranking Window field reports for a blank, sub-minute or between-minutes
    // entry. Caught here because `Number(null)` is 0, which the server would reject with a 400
    // that surfaces as the generic failure alert below.
    if (settings.gloryBoardWindowHours === null) {
      alert('GloryBoard Ranking Window must be at least 1 minute, in whole minutes.');
      return;
    }
    // The server rejects anything below 1 with a 400, which would surface as the generic alert.
    const referralThreshold = Number(settings.referralMilestoneThreshold);
    if (!Number.isInteger(referralThreshold) || referralThreshold < 1) {
      alert('Referral Milestone "Trigger After" must be a whole number of at least 1.');
      return;
    }
    // `null` is what the snooze field reports for an invalid entry; the field shows why.
    if (settings.referralPromptSnoozeMinutes === null) {
      alert(t('admin.referralSnooze.saveInvalid'));
      return;
    }
    // Asked last, once everything else is known to save: the new length re-times running windows.
    const windowChange = windowChangeConfirmation(savedWindowHours, settings.gloryBoardWindowHours);
    if (windowChange && !window.confirm(windowChange)) return;

    try {
      setSaving(true);
      const [rewardRes] = await Promise.all([
        Api.put('/admin/rewardPointSetting/update', {
          htAccuracyPoints: {
            exactMatch: Number(settings.htAccuracyPoints.exactMatch),
            goalDiff: Number(settings.htAccuracyPoints.goalDiff),
            correctWinner: Number(settings.htAccuracyPoints.correctWinner),
            totalGoals: Number(settings.htAccuracyPoints.totalGoals)
          },
          ftClosenessPoints: {
            correctWinner: Number(settings.ftClosenessPoints.correctWinner),
            goalDiff: Number(settings.ftClosenessPoints.goalDiff),
            totalGoals: Number(settings.ftClosenessPoints.totalGoals)
          },
          ftExactScore: Number(settings.ftExactScore),
          endOfVoteHalfTime: Number(settings.endOfVoteHalfTime),
          endOfVoteFullTime: Number(settings.endOfVoteFullTime),
          htPredictionParticipationPoints: Number(settings.htPredictionParticipationPoints),
          ftPredictionParticipationPoints: Number(settings.ftPredictionParticipationPoints),
          liveCheckInPoints: Number(settings.liveCheckInPoints),
          fanCamChallengePoints: Number(settings.fanCamChallengePoints),
          fanCamVideoPoints: Number(settings.fanCamVideoPoints),
          fanCamLivePoints: Number(settings.fanCamLivePoints),
          quizParticipationPoints: Number(settings.quizParticipationPoints),
          quizPerfectScoreBonus: Number(settings.quizPerfectScoreBonus),
          shoutoutPoints: Number(settings.shoutoutPoints),
          potmVotePoints: Number(settings.potmVotePoints),
          gloryBoardWindowHours: Number(settings.gloryBoardWindowHours),
          referralMilestoneThreshold: Number(settings.referralMilestoneThreshold),
          referralBonusPoints: Number(settings.referralBonusPoints),
          referralPromptSnoozeMinutes: Number(settings.referralPromptSnoozeMinutes),
          tiri: {
            participationPoints: Number(settings.tiri.participationPoints),
            // `tagit` is deliberately absent, and `rateit` carries only the referral: the
            // per-module participation keys are superseded, and the server `$set`s whatever
            // this object holds — so sending them would keep resurrecting dead data.
            rateit: {
              referralPoints: Number(settings.tiri.rateit.referralPoints)
            }
          },
          countryExclusions: Object.fromEntries(
            Object.entries(settings.countryExclusions).map(([k, v]) => [k.toLowerCase(), v])
          ),
          teamExclusions: settings.teamExclusions
        }),
        Api.put('/admin/badgeSettings/update', {
          tiers: {
            bronze: {
              minPoints: 0,
              maxPoints: Number(badgeSettings.tiers.bronze.maxPoints)
            },
            silver: {
              minPoints: Number(badgeSettings.tiers.silver.minPoints),
              maxPoints: Number(badgeSettings.tiers.silver.maxPoints)
            },
            gold: {
              minPoints: Number(badgeSettings.tiers.gold.minPoints)
            }
          }
        })
      ]);
      const appliedWindows = describeAppliedWindows(rewardRes?.data?.gloryBoardWindows);
      alert(`Settings updated successfully${appliedWindows ? `\n\n${appliedWindows}` : ''}`);
      fetchSettings();
      fetchBadgeSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleNestedChange = (parent, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [key]: value }
    }));
  };

  const handleFlatChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  /** A field at the TIRI root — the participation payout both modules share. */
  const handleTiriChange = (key, value) => {
    setSettings((prev) => ({ ...prev, tiri: { ...prev.tiri, [key]: value } }));
  };

  /** A field under one module — only Rate It has any left, and only the referral payout. */
  const handleTiriModuleChange = (module, key, value) => {
    setSettings((prev) => ({
      ...prev,
      tiri: { ...prev.tiri, [module]: { ...prev.tiri[module], [key]: value } }
    }));
  };

  const handleBadgeTierChange = (tier, key, value) => {
    setBadgeSettings((prev) => {
      const tiers = {
        ...prev.tiers,
        [tier]: { ...prev.tiers[tier], [key]: value }
      };

      // Keep tiers contiguous: next tier's min always follows this max.
      const numeric = Number(value);
      const isValidNumber = value !== '' && !Number.isNaN(numeric);
      if (tier === 'bronze' && key === 'maxPoints' && isValidNumber) {
        tiers.silver = { ...tiers.silver, minPoints: numeric + 1 };
      } else if (tier === 'silver' && key === 'maxPoints' && isValidNumber) {
        tiers.gold = { ...tiers.gold, minPoints: numeric + 1 };
      }

      return { ...prev, tiers };
    });
  };

  const handleAddCountry = () => {
    const key = countryInput.trim().toLowerCase();
    if (!key) return;
    setSettings((prev) => ({
      ...prev,
      countryExclusions: { ...prev.countryExclusions, [key]: prev.countryExclusions[key] || {} }
    }));
    setCountryInput('');
  };

  const handleRemoveCountry = (key) => {
    setSettings((prev) => {
      const { [key]: _removed, ...rest } = prev.countryExclusions;
      return { ...prev, countryExclusions: rest };
    });
  };

  const handleCountryExclusionChange = (key, category, val) => {
    setSettings((prev) => ({
      ...prev,
      countryExclusions: {
        ...prev.countryExclusions,
        [key]: { ...(prev.countryExclusions[key] || {}), [category]: val }
      }
    }));
  };

  const handleAddTeam = () => {
    const key = teamInput.trim().toLowerCase();
    if (!key) return;
    setSettings((prev) => ({
      ...prev,
      teamExclusions: { ...prev.teamExclusions, [key]: prev.teamExclusions[key] || {} }
    }));
    setTeamInput('');
  };

  const handleRemoveTeam = (teamId) => {
    setSettings((prev) => {
      const { [teamId]: _removed, ...rest } = prev.teamExclusions;
      return { ...prev, teamExclusions: rest };
    });
  };

  const handleTeamExclusionChange = (teamId, category, val) => {
    setSettings((prev) => ({
      ...prev,
      teamExclusions: {
        ...prev.teamExclusions,
        [teamId]: { ...(prev.teamExclusions[teamId] || {}), [category]: val }
      }
    }));
  };

  if (loading) {
    return (
      <Col wid="100%" ht="100vh" centerAll>
        <Spinner2 />
      </Col>
    );
  }

  const groupedFlatFields = FLAT_FIELDS.reduce((acc, field) => {
    if (!acc[field.section]) acc[field.section] = [];
    acc[field.section].push(field);
    return acc;
  }, {});

  const activeCountryRows = Object.keys(settings.countryExclusions).map((key) => ({
    id: key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
    type: 'country',
    excl: settings.countryExclusions[key] || {}
  }));

  const activeTeamRows = Object.keys(settings.teamExclusions).map((username) => ({
    id: username,
    label: `@${username}`,
    type: 'team',
    excl: settings.teamExclusions[username] || {}
  }));

  const restrictionRows = [...activeCountryRows, ...activeTeamRows];

  return (
    <Col wid="100%" pad="24px" gap="32px">
      {/* HT Accuracy Points */}
      <Section title={SECTIONS.htAccuracy.title} description={SECTIONS.htAccuracy.description}>
        <Row wrap="wrap" gap="16px">
          {SECTIONS.htAccuracy.fields.map(({ key, label, desc }) => (
            <FieldInput
              key={key}
              label={label}
              description={desc}
              value={settings.htAccuracyPoints[key]}
              onChange={(val) => handleNestedChange('htAccuracyPoints', key, val)}
            />
          ))}
        </Row>
      </Section>

      {/* FT Closeness Points */}
      <Section title={SECTIONS.ftCloseness.title} description={SECTIONS.ftCloseness.description}>
        <Row wrap="wrap" gap="16px">
          <FieldInput
            label="Exact Score"
            description="Points for exact FT prediction"
            value={settings.ftExactScore}
            onChange={(val) => handleFlatChange('ftExactScore', val)}
          />
          {SECTIONS.ftCloseness.fields.map(({ key, label, desc }) => (
            <FieldInput
              key={key}
              label={label}
              description={desc}
              value={settings.ftClosenessPoints[key]}
              onChange={(val) => handleNestedChange('ftClosenessPoints', key, val)}
            />
          ))}
        </Row>
      </Section>

      {/* Flat Fields grouped by section */}
      {Object.entries(groupedFlatFields).map(([sectionName, fields]) => (
        <Section key={sectionName} title={`${sectionName} Points`}>
          <Row wrap="wrap" gap="16px">
            {fields.map(({ key, label, desc }) => (
              <FieldInput
                key={key}
                label={label}
                description={desc}
                value={settings[key]}
                onChange={(val) => handleFlatChange(key, val)}
              />
            ))}
          </Row>
        </Section>
      ))}

      {/* GloryBoard — its own card because the window takes a unit, not just a number */}
      <Section title="GloryBoard Points">
        <WindowLengthInput
          hours={settings.gloryBoardWindowHours}
          onChange={(hours) => handleFlatChange('gloryBoardWindowHours', hours)}
        />
      </Section>

      {/* Referral Milestone (sender-side "invite a friend" flow) */}
      <Section
        title="Referral Milestone"
        description="Fan invite prompt shown after watching a team's FanCam moments (per team, not shared across teams)"
      >
        <Row wrap="wrap" gap="16px">
          <FieldInput
            label="Trigger After"
            description="Fires every N FanCam moments watched (N, 2N, 3N…; at least 1)"
            value={settings.referralMilestoneThreshold}
            onChange={(val) => handleFlatChange('referralMilestoneThreshold', val)}
          />
          <FieldInput
            label="Bonus Points"
            description="Awarded to both fans once the referred friend completes the challenge"
            value={settings.referralBonusPoints}
            onChange={(val) => handleFlatChange('referralBonusPoints', val)}
          />
        </Row>
      </Section>

      {/* Invite prompt snooze — its own card, not under Referral Milestone, because it governs the
          invite prompts of BOTH FanCam and Rate It. */}
      <Section
        title={t('admin.referralSnooze.title')}
        description={t('admin.referralSnooze.description')}
      >
        {/* Title and description stay left like every other card; the field is centred, with its
            label just above the input (see `SnoozeLengthInput`). */}
        <SnoozeLengthInput
          minutes={settings.referralPromptSnoozeMinutes}
          onChange={(minutes) => handleFlatChange('referralPromptSnoozeMinutes', minutes)}
        />
      </Section>

      {/* TIRI (Tag It Rate It) — ONE card, no per-module sub-cards.
          It used to be a sub-card per module, because each priced its own participation cycle.
          They now share one cycle, so a per-module split would invite an admin to set two
          numbers for a payout that can only have one — and a fan's 3-tags-plus-2-ratings cycle
          would have no answer to which of them applied. */}
      <Section
        title={t('admin.tiri.settings.title')}
        description={t('admin.tiri.settings.description')}
      >
        <Row wrap="wrap" gap="16px">
          <FieldInput
            label={t('admin.tiri.settings.participationPoints')}
            description={t('admin.tiri.settings.participationPointsDesc')}
            value={settings.tiri.participationPoints}
            onChange={(val) => handleTiriChange('participationPoints', val)}
          />
          <FieldInput
            label={t('admin.tiri.settings.referralPoints')}
            description={t('admin.tiri.settings.referralPointsDesc')}
            value={settings.tiri.rateit.referralPoints}
            onChange={(val) => handleTiriModuleChange('rateit', 'referralPoints', val)}
          />
        </Row>
      </Section>

      {/* Badge Thresholds */}
      <Section
        title="Badge Thresholds"
        description="Minimum points required to reach each badge level"
      >
        <Row wrap="wrap" gap="16px">
          <FieldInput
            label="Bronze Max"
            description="Max points for Bronze"
            value={badgeSettings.tiers.bronze.maxPoints}
            onChange={(val) => handleBadgeTierChange('bronze', 'maxPoints', val)}
          />
          <FieldInput
            label="Silver Min"
            description="Auto-set to Bronze Max + 1"
            value={badgeSettings.tiers.silver.minPoints}
            onChange={(val) => handleBadgeTierChange('silver', 'minPoints', val)}
            disabled
          />
          <FieldInput
            label="Silver Max"
            description="Max points for Silver"
            value={badgeSettings.tiers.silver.maxPoints}
            onChange={(val) => handleBadgeTierChange('silver', 'maxPoints', val)}
          />
          <FieldInput
            label="Gold Min"
            description="Auto-set to Silver Max + 1 (no max)"
            value={badgeSettings.tiers.gold.minPoints}
            onChange={(val) => handleBadgeTierChange('gold', 'minPoints', val)}
            disabled
          />
        </Row>
      </Section>

      {/* Restrictions */}
      <Section
        title="Restrictions"
        description="Disable point categories for users from specific countries or football clubs"
      >
        {restrictionRows.length > 0 && (
          <ExclusionGrid
            rows={restrictionRows}
            onToggle={(row, cat, val) =>
              row.type === 'country'
                ? handleCountryExclusionChange(row.id, cat, val)
                : handleTeamExclusionChange(row.id, cat, val)
            }
            onRemove={(row) =>
              row.type === 'country'
                ? handleRemoveCountry(row.id)
                : handleRemoveTeam(row.id)
            }
          />
        )}

        <Row gap="24px" marg="16px 0 0" style={{ alignItems: 'flex-end' }}>
          {/* Add country */}
          <Col style={{ flex: 1 }}>
            <Text b2 bold marg="0 0 4px">Add country</Text>
            <Row gap="8px" style={{ alignItems: 'center' }}>
              <Input
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value)}
                placeholder="e.g. Japan"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCountry()}
              />
              <Button
                text="Add"
                onClick={handleAddCountry}
                bg={theme.PRIMARY}
                wid="80px"
              />
            </Row>
          </Col>

          {/* Add team */}
          <Col style={{ flex: 1 }}>
            <Text b2 bold marg="0 0 4px">Add team</Text>
            <Row gap="8px" style={{ alignItems: 'center' }}>
              <Input
                value={teamInput}
                onChange={(e) => setTeamInput(e.target.value)}
                placeholder="Team username"
                onKeyDown={(e) => e.key === 'Enter' && handleAddTeam()}
              />
              <Button
                text="Add"
                onClick={handleAddTeam}
                bg={theme.PRIMARY}
                wid="80px"
              />
            </Row>
            <Text c1 col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
              Must match exact team username. Invalid entries have no effect.
            </Text>
          </Col>
        </Row>
      </Section>

      {/* Save All */}
      <Row marg="0 0 48px">
        <Button
          text={saving ? 'Updating...' : 'Save Changes'}
          onClick={handleSaveAll}
          bg={theme.PRIMARY}
          loading={saving}
          wid="200px"
        />
      </Row>
    </Col>
  );
};

const ExclusionGrid = ({ rows, onToggle, onRemove }) => (
  <Col gap="0" marg="0 0 8px" style={{alignSelf: "center"}} >
    <Row
      pad="8px 0"
      style={{ borderBottom: `1px solid ${theme.GREY_20 || '#eee'}` }}
    >
      <Col style={{ flex: '1 1 160px', minWidth: '140px' }}>
        <Text b2 bold marg="0" col={theme.GREY_60}>Country / Team</Text>
      </Col>
      {TOGGLE_CATEGORIES.map((cat) => (
        <Col centerAll key={cat.key} style={{ flex: '0 0 130px' }}>
          <Text b2 bold marg="0" col={theme.GREY_60} style={{ textAlign: 'center' }}>{cat.label}</Text>
        </Col>
      ))}
      <Col style={{ flex: '0 0 40px' }} />
    </Row>
    {rows.map((row) => (
      <Row
        center
        key={row.id}
        pad="10px 0"
        style={{ borderBottom: `1px solid ${theme.GREY_10 || '#f5f5f5'}` }}
      >
        <Col style={{ flex: '1 1 160px', minWidth: '140px' }}>
          <Text b2 marg="0">{row.label}</Text>
          <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '11px' }}>
            {row.type === 'country' ? 'Country' : 'Team'}
          </Text>
        </Col>
        {TOGGLE_CATEGORIES.map((cat) => (
          <Col centerAll key={cat.key} style={{ flex: '0 0 130px' }}>
            <Toggle
              on={!!row.excl[cat.key]}
              onToggle={() => onToggle(row, cat.key, !row.excl[cat.key])}
            />
          </Col>
        ))}
        <Col centerAll style={{ flex: '0 0 40px' }}>
          <div
            onClick={() => onRemove(row)}
            style={{ cursor: 'pointer', color: theme.GREY_60 || '#999', fontSize: '18px', lineHeight: 1 }}
          >
            ×
          </div>
        </Col>
      </Row>
    ))}
  </Col>
);

const FieldInput = ({ label, description, value, onChange, step, disabled }) => (
  <Col style={{ flex: '1 1 140px', minWidth: '120px', maxWidth: '180px' }}>
    <Text b2 bold marg="0 0 4px">
      {label}
    </Text>
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      step={step}
      disabled={disabled}
      bg={disabled ? theme.GREY_30 : undefined}
      style={disabled ? {cursor: 'not-allowed'} : undefined}
    />
    {description && (
      <Text c1 col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
        {description}
      </Text>
    )}
  </Col>
);

/**
 * The GloryBoard window length as an amount + unit. The amount and unit live here, as typed; the
 * parent only ever sees hours (or `null` while the entry is not a valid length), which is the one
 * figure the server stores. Seeded once on mount — the tab remounts this after every fetch.
 */
const WindowLengthInput = ({ hours, onChange }) => {
  const [{ amount, unit }, setEntry] = useState(() => windowFromHours(hours));

  const update = (next) => {
    setEntry(next);
    onChange(windowToHours(next.amount, next.unit));
  };

  const minutes = windowMinutes(amount, unit);
  const unitIdx = WINDOW_UNITS.findIndex((u) => u.key === unit);

  return (
    <Col style={{ maxWidth: '360px' }}>
      <Text b2 bold marg="0 0 4px">
        Ranking Window
      </Text>
      <Row gap="12px" style={{ alignItems: 'center' }}>
        <Input
          type="number"
          min="0"
          step="any"
          wid="120px"
          value={amount}
          onChange={(e) => update({ amount: e.target.value, unit })}
          placeholder="Length"
        />
        <SegmentControl
          options={WINDOW_UNITS.map((u) => u.label)}
          selectedIdx={unitIdx}
          handleSelect={(i) => update({ amount, unit: WINDOW_UNITS[i].key })}
          small
          wid="220px"
          textStyle={{ size: '13px' }}
        />
      </Row>
      {minutes === null ? (
        <Text c1 col={theme.SECONDARY} marg="4px 0 0" style={{ fontSize: '11px' }}>
          Enter a length of at least 1 minute, in whole minutes.
        </Text>
      ) : (
        <Text c1 col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
          Runs for {describeMinutes(minutes)} from a match’s halftime.
        </Text>
      )}
      <Text c1 col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
        Applies as soon as you save, to windows already running as well as the next one. A window
        that has already run longer than the new length closes at the save.
      </Text>
    </Col>
  );
};

/**
 * The invite prompt snooze as an amount + Min/Hours/Days. Like `WindowLengthInput`, the amount and
 * unit live here as typed; the parent only sees whole minutes (or `null` while the entry is not a
 * valid length), which is what the server stores. Seeded once on mount.
 */
const SnoozeLengthInput = ({ minutes, onChange }) => {
  const { t } = useTranslation();
  const [{ amount, unit }, setEntry] = useState(() => snoozeFromMinutes(minutes));

  const update = (next) => {
    setEntry(next);
    onChange(snoozeMinutes(next.amount, next.unit));
  };

  const total = snoozeMinutes(amount, unit);
  const error = snoozeError(amount, unit);
  const unitIdx = SNOOZE_UNITS.findIndex((u) => u.key === unit);

  return (
    <Col noFlex wid="100%" centerAll>
      {/* Shrink-wrapped and centred, so the label starts exactly above the input box. */}
      <Col noFlex center>
        <Text b2 bold marg="0 0 4px">
          {t('admin.referralSnooze.label')}
        </Text>
        <Row gap="12px" style={{ alignItems: 'center' }}>
          <Input
            type="number"
            min="0"
            step="any"
            wid="120px"
            value={amount}
            onChange={(e) => update({ amount: e.target.value, unit })}
            placeholder={t('admin.referralSnooze.placeholder')}
          />
          <SegmentControl
            options={SNOOZE_UNITS.map((u) => t(`admin.referralSnooze.units.${u.key}`))}
            selectedIdx={unitIdx}
            handleSelect={(i) => update({ amount, unit: SNOOZE_UNITS[i].key })}
            small
            wid="220px"
            textStyle={{ size: '13px' }}
          />
        </Row>
      </Col>
      {error ? (
        <Text c1 center col={theme.SECONDARY} marg="4px 0 0" style={{ fontSize: '11px' }}>
          {t(`admin.referralSnooze.errors.${error}`)}
        </Text>
      ) : (
        <Text c1 center col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
          {t('admin.referralSnooze.hint', { length: describeSnooze(total, t, unit) })}
        </Text>
      )}
    </Col>
  );
};

export default RewardPointsTab;
